import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import LogoutIcon from '@mui/icons-material/Logout';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import { BsGrid3X3GapFill } from "react-icons/bs";
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from "react-icons/fa6";
import { TextField, InputAdornment, IconButton, capitalize } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { deleteCustomerById, getAllCustomers, getAllUserData, updateCustomerRole } from '../Api';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { toast } from 'react-toastify';
import { FaFileAlt, FaLocationArrow } from "react-icons/fa";
import { BiSolidCoupon } from 'react-icons/bi';

// Navigation configuration
const NAVIGATION = [
  {
    kind: 'header',
    title: 'Main items',
  },
  {
    segment: 'dashboard',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    segment: 'dashboard-products',
    title: 'Products',
    icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-users',
    title: 'Users',
    icon: <HiUsers style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-categories',
    title: 'Categories',
    icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-orders',
    title: 'Orders',
    icon: <ShoppingCartIcon />,
  },
  {
    segment: 'prescriptions',
    title: 'Prescriptions',
    icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-coupons',
    title: 'Coupons',
    icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-content',
    title: 'Content',
    icon: <FaFileAlt style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-location',
    title: 'Location',
    icon: <FaLocationArrow />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Analytics',
  },
  {
    segment: 'reports',
    title: 'Reports',
    icon: <BarChartIcon />,
    children: [
      {
        segment: 'sales',
        title: 'Sales',
        icon: <DescriptionIcon />,
      },
      {
        segment: 'traffic',
        title: 'Traffic',
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    segment: 'integrations',
    title: 'Integrations',
    icon: <LayersIcon />,
  },
];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  height: 230,
  bgcolor: "background.paper",
  border: "1px solid transparent",
  borderRadius: 1,
  shadow: 24,
  p: 4,
};

// Styled Skeleton component
const Skeleton = styled('div')(({ theme, height }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  height,
  content: '" "',
}));

// Custom router hook
function useDemoRouter(initialPath) {
  const [pathname, setPathname] = React.useState(initialPath);
  const navigate = useNavigate();

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => {
        setPathname(String(path));
        navigate(path);
      },
    };
  }, [pathname, navigate]);

  return router;
}

// Create a light theme
const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

function DshUsers({ onLogout }) {
  const navigate = useNavigate();
  const router = useDemoRouter('/dashboard-users');
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [searchTerm, setSearchTerm] = useState('');
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  // Pending privileged role change awaiting confirmation (SEC-4). Null when none.
  const [roleConfirm, setRoleConfirm] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [role, setRole] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const filteredCustomers = customers.filter((user) =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCustomers = filteredCustomers.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleSearchClick = () => {
    console.log('Searching for:', searchTerm);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      try {
        const data = await getAllUserData(1, 50);
        setCustomers(Array.isArray(data.data) ? data.data : []);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
      } catch (error) {
        console.error('Error fetching customers:', error);
        setCustomers([]);
        setTotalPages(1);
        setSnackbar({
          open: true,
          message: 'Failed to fetch customers',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [token, itemsPerPage]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredCustomers.length / itemsPerPage));
  }, [filteredCustomers, itemsPerPage]);

  const handleOpen = (userId, currentRole) => {
    setSelectedUserId(userId);
    setRole(currentRole);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
    setRole('');
  };

  const handleOpen2 = () => {
    setOpen2(true);
  };

  const handleClose2 = () => {
    setOpen2(false);
  };

  async function deleteCustomer(e, userId) {
    e.preventDefault();

    if (window.confirm("Are you sure want to delete this user?")) {
      setLoading(true)
      try {
        // BUG-8, three defects in these few lines:
        //  1. `deleteCustomerById` was not awaited, so the refetch below raced
        //     the delete and the user usually reappeared in the list.
        //  2. The success toast sat in a `finally`, so it fired even when the
        //     delete rejected or the refetch failed — "deleted successfully"
        //     shown next to an error snackbar.
        //  3. `data.length` is undefined (the array is `data.data`), making
        //     totalPages NaN. Dropped entirely: the effect watching
        //     `filteredCustomers` already derives totalPages correctly.
        await deleteCustomerById(userId)
        toast.success("User deleted successfully")

        try {
          const data = await getAllCustomers(token);
          setCustomers(Array.isArray(data.data) ? data.data : []);
        } catch (error) {
          console.error('Error fetching customers:', error);
          // The delete did succeed — only the refresh failed, so say that
          // rather than implying the user is still there.
          setSnackbar({
            open: true,
            message: 'User deleted, but the list could not be refreshed.',
            severity: 'warning',
          });
        }
      } catch (error) {
        console.log(error)
        setSnackbar({
          open: true,
          message: 'Failed to delete the user. They have not been removed.',
          severity: 'error',
        });
      } finally {
        setLoading(false)
      }
    }
  }

  // SEC-4: roles that grant more than ordinary customer access. Promoting someone
  // here is a privilege grant, not a profile edit — `administrator` in particular
  // means full control of the WordPress install, including the ability to change
  // every other account. Submitting the form used to apply it immediately, so a
  // mis-click on an adjacent <option> silently created an admin.
  const PRIVILEGED_ROLES = {
    administrator: 'full control of the store and every account in it, including yours',
    shop_manager: 'management of all products, orders and customer records',
    editor: 'the ability to publish and edit any content on the site',
  };

  // Applies the change. Separated from the form handler so the confirmation
  // dialog can call it directly once the operator has confirmed.
  const applyRoleChange = async (targetRole, targetUserId) => {
    if (!targetUserId || !targetRole) return;
    setRoleConfirm(null);
    handleClose()
    setLoading(true)

    try {
      await updateCustomerRole(targetUserId, targetRole);
      setCustomers((prev) =>
        prev.map((user) =>
          user.id === targetUserId ? { ...user, role: targetRole } : user
        )
      );
      setSnackbar({
        open: true,
        message: 'Role updated successfully',
        severity: 'success',
      });
      handleClose();
    } catch (error) {
      console.error('Error updating role:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update role',
        severity: 'error',
      });
    } finally {
      setLoading(false)
    }
  };

  const updateRole = async (e) => {
    e.preventDefault();
    if (!selectedUserId || !role) return;

    // Privileged target: stop and ask, capturing the id alongside the role so the
    // confirmation acts on what was actually reviewed even if the table reloads.
    if (PRIVILEGED_ROLES[role]) {
      const target = customers.find((user) => user.id === selectedUserId);
      setRoleConfirm({
        userId: selectedUserId,
        role,
        // Same fields the table itself renders, so the dialog names the person
        // the operator is looking at rather than an id they cannot place.
        userLabel: target
          ? target.name || target.username || target.email || `user ${target.id}`
          : `user ${selectedUserId}`,
      });
      return;
    }

    await applyRoleChange(role, selectedUserId);
  };

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true)
  }

  const handleLogoutConfirm = () => {
    setOpenLogoutDialog(false);
    onLogout();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Custom branding with logo
  const branding = {
    logo: (
      <div className="header-logo">
        <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
      </div>
    ),
    title: '',
  };

  // Custom toolbar actions for the header
  const ToolbarActions = () => (
    <Button
      variant="outlined"
      startIcon={<LogoutIcon />}
      onClick={handleLogoutClick}
      sx={{ marginRight: 2 }}
      style={{ background: "#0165AB", color: "#fff" }}
    >
      Logout
    </Button>
  );

  return (
    <>
      <AppProvider
        navigation={NAVIGATION}
        router={router}
        theme={theme}
        branding={branding}
      >
        <DashboardLayout
          slots={{
            toolbarActions: ToolbarActions,
          }}
        >
          <PageContainer>
            {loading ? (
              <Grid container spacing={1}>
                <Grid size={12} height={5} />
                <Grid size={4}>
                  <Skeleton height={40} />
                </Grid>
                <Grid size={8}>
                  <Skeleton height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton height={500} />
                </Grid>
              </Grid>
            ) : (
              <section className="admin-dashboard">
                <Grid container spacing={1}>
                  <Grid size={12} height={5} />
                  <Grid size={4}>
                    <div style={{ height: "40px" }}>
                      <TextField
                        id="search-field"
                        type="search"
                        style={{ width: "100%" }}
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton aria-label="search" onClick={handleSearchClick}>
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid size={8} />
                  {/* <Grid size={2}>
                    <button style={{
                      background: "#0165AB", color: "#fff", width: "100%", height: "40px", border: "1px solid transparant",
                      borderRadius: "5px", cursor: "pointer", fontWeight: "600", letterSpacing: "0.5px"
                    }}>{`Add user`.toLocaleUpperCase()}</button>
                  </Grid> */}
                  <Grid size={12}>
                    <table className="user-detail-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Email</th>
                          <th>Username</th>
                          <th>Role</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCustomers.length > 0 ? (
                          paginatedCustomers.map((user) => (
                            <tr key={user.id}>
                              <td style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <img
                                  src={user.avatar_url || ''}
                                  alt={user.name}
                                  style={{ width: '32px', height: '32px', margin: "auto", borderRadius: '50%' }}
                                />
                                {user.name}
                              </td>
                              <td>{user.email}</td>
                              <td>{user.username}</td>
                              <td>{capitalize(user.role)}</td>
                              <td style={{ width: "240px" }}>
                                <div className="d-flex">
                                  <button
                                    style={{
                                      height: "30px",
                                      width: "100px",
                                      border: "1px solid transparent",
                                      borderRadius: "5px",
                                      background: "#0165AB",
                                      color: "#fff",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => handleOpen(user.id, user.role)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    style={{
                                      height: "30px",
                                      width: "100px",
                                      border: "1px solid transparent",
                                      borderRadius: "5px",
                                      background: "#0165AB",
                                      color: "#fff",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      marginLeft: "20px",
                                    }}
                                    onClick={(e) => deleteCustomer(e, user.id)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5">No users found!</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Grid>
                </Grid>
                <Stack spacing={2} sx={{ marginTop: 2, alignItems: 'center' }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Stack>
              </section>
            )}
            <Modal
              open={open}
              onClose={handleClose}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box sx={style}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  Edit User Role
                </Typography>
                <Box id="modal-modal-description" sx={{ mt: 2 }}>
                  <form onSubmit={updateRole}>
                    <div className="product-input-groups">
                      <label htmlFor="role">Role</label>
                      <br />
                      <select
                        onChange={(e) => setRole(e.target.value)}
                        name="role"
                        required
                        style={{ height: "30px", width: "100%", margin: "0 0 20px" }}
                        value={role}
                      >
                        <option value="shop_manager">Shop Manager</option>
                        <option value="customer">Customer</option>
                        <option value="subscriber">Subscriber</option>
                        <option value="contributor">Contributor</option>
                        <option value="author">Author</option>
                        <option value="editor">Editor</option>
                        <option value="administrator">Administrator</option>
                      </select>
                    </div>
                    <div className="d-flex">
                      <button
                        type="button"
                        style={{
                          height: "35px",
                          width: "100px",
                          border: "1px solid transparent",
                          background: "red",
                          color: "#fff",
                          borderRadius: "5px",
                          cursor: "pointer",
                        }}
                        onClick={handleClose}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{
                          height: "35px",
                          width: "100px",
                          margin: "0 0 0 20px",
                          border: "1px solid transparent",
                          background: "#0165AB",
                          color: "#fff",
                          borderRadius: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Update
                      </button>
                    </div>
                  </form>
                </Box>
              </Box>
            </Modal>
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbar.severity}
                sx={{ width: '100%' }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </PageContainer>
        </DashboardLayout>
        {/* SEC-4: confirmation for privilege-granting role changes. */}
        <Dialog
          open={Boolean(roleConfirm)}
          onClose={() => setRoleConfirm(null)}
          aria-labelledby="role-confirm-title"
          aria-describedby="role-confirm-description"
        >
          <DialogTitle id="role-confirm-title">
            Grant {roleConfirm ? capitalize(roleConfirm.role.replace('_', ' ')) : ''} access?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="role-confirm-description">
              This gives <strong>{roleConfirm?.userLabel}</strong>{' '}
              {roleConfirm ? PRIVILEGED_ROLES[roleConfirm.role] : ''}.
              {roleConfirm?.role === 'administrator' && (
                <>
                  {' '}An administrator can remove your own access and cannot be
                  undone by anyone with less privilege.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRoleConfirm(null)} autoFocus>Cancel</Button>
            <Button
              color="error"
              onClick={() => applyRoleChange(roleConfirm.role, roleConfirm.userId)}
            >
              Grant access
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openLogoutDialog}
          onClose={handleLogoutCancel}
          aria-labelledby="logout-dialog-title"
          aria-describedby="logout-dialog-description"
        >
          <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
          <DialogContent>
            <DialogContentText id="logout-dialog-description">
              Are you sure you want to log out?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLogoutCancel}>Cancel</Button>
            <Button onClick={handleLogoutConfirm} autoFocus>
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </AppProvider>
    </>
  );
}

export default DshUsers;